// alterUser — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-user' })` op (DSE2 §3.F1).

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, UserChange } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterUserArgs {
  readonly userName: string;
  readonly quoted?: boolean;
  readonly mysqlHost?: string;
  readonly change: UserChange;
}

export function buildAlterUserEdit(args: AlterUserArgs): DdlEdit {
  return {
    op: 'alter-user',
    userName: args.userName,
    ...(args.quoted ? { quoted: true } : {}),
    ...(args.mysqlHost !== undefined ? { mysqlHost: args.mysqlHost } : {}),
    change: args.change,
  };
}

export async function commitAlterUser(
  ctx: VqeDdlEditContext,
  args: AlterUserArgs,
): Promise<void> {
  const edit = buildAlterUserEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_USER_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'mysql', 'oracle',
] as const);

export function isAlterUserSupported(engine: SqlEngine): boolean {
  return ALTER_USER_SUPPORTED.has(engine);
}

export function getAlterUserUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') return 'SQLite has no auth model.';
  if (engine === 'postgres') return 'PostgreSQL has no separate USER — use ALTER ROLE.';
  return '';
}

export function isNonEmptyUserChange(change: UserChange): boolean {
  return Boolean(
    change.mssqlDefaultSchema
      || change.mssqlName
      || change.mysqlIdentifiedBy
      || change.mysqlPasswordExpireText
      || change.oracleIdentifiedBy
      || change.oracleAccountLocked !== undefined,
  );
}

export function validateAlterUserForEngine(
  engine: SqlEngine,
  args: AlterUserArgs,
): string | null {
  if (!isAlterUserSupported(engine)) {
    return getAlterUserUnsupportedTooltip(engine);
  }
  if (args.userName.trim().length === 0) {
    return 'User name is required.';
  }
  if (!isNonEmptyUserChange(args.change)) {
    return 'Pick at least one change.';
  }
  if (engine !== 'mssql' && (args.change.mssqlDefaultSchema || args.change.mssqlName)) {
    return `${engine} does not accept mssql-only ALTER USER options.`;
  }
  if (engine !== 'mysql' && (args.change.mysqlIdentifiedBy || args.change.mysqlPasswordExpireText)) {
    return `${engine} does not accept mysql-only ALTER USER options.`;
  }
  if (engine !== 'oracle' && (args.change.oracleIdentifiedBy
      || args.change.oracleAccountLocked !== undefined)) {
    return `${engine} does not accept oracle-only ALTER USER options.`;
  }
  return null;
}
